/** Gaveta da Expedição: a lista de pedidos de qualquer número da tela, com exportação para o Excel. */
import { useMemo } from 'react'
import {
  Box, Button, Chip, Drawer, IconButton, Link, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from '@mui/material'
import type { Pedido } from './types'
import {
  COLUNAS_LISTA, COR_ETAPA, ROTULO_ALERTA, ROTULO_ETAPA, csvPedidos, dataHoraSP, etapa,
  linkMarketplace, numerarVolumes, ordenarLista, resumoPedidos, rotuloStatusMk, tagPedido,
} from './logic'

export type ListaAberta = { titulo: string; pedidos: Pedido[] }

function baixar(nome: string, texto: string) {
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/csv;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = nome
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

const nomeArquivo = (titulo: string) =>
  `expedicao_${titulo.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^\w]+/g, '_').replace(/^_|_$/g, '').toLowerCase()}.csv`


export function Gaveta({ lista, onClose }: { lista: ListaAberta | null; onClose: () => void }) {
  const ps = useMemo(() => (lista ? ordenarLista(lista.pedidos) : []), [lista])
  const volumes = useMemo(() => numerarVolumes(ps), [ps])
  return (
    <Drawer anchor="right" open={!!lista} onClose={onClose} sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
      slotProps={{ paper: { sx: { width: { xs: '100vw', md: 'min(1100px, 92vw)' } } } }}>
      {lista && (
        <Stack sx={{ height: '100%' }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ flex: 1 }}>
              {lista.titulo} · {resumoPedidos(ps)}
            </Typography>
            <Button size="small" variant="outlined" disabled={!ps.length}
              onClick={() => baixar(nomeArquivo(lista.titulo), csvPedidos(ps))}>Exportar Excel</Button>
            <IconButton aria-label="fechar" onClick={onClose}>✕</IconButton>
          </Stack>
          <Box sx={{ overflow: 'auto', flex: 1, px: 1 }}>
            {!ps.length ? (
              <Typography color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Nenhum pedido.</Typography>
            ) : (
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {COLUNAS_LISTA.map((c) => <TableCell key={c} sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{c}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ps.map((p) => {
                    const link = linkMarketplace(p)
                    const atrasado = p.alertas.includes('atrasado')
                    const e = etapa(p)
                    const tag = tagPedido(p)
                    return (
                      <TableRow key={p.id} hover>
                        <TableCell>{p.nota || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {link ? <Link href={link} target="_blank" rel="noopener noreferrer">{p.pedido_marketplace}</Link> : (p.pedido_marketplace || '—')}
                          {volumes.has(p.id) && <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.2 }}>{volumes.get(p.id)}</Typography>}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.canal_rotulo}</TableCell>
                        <TableCell>{p.destinatario}</TableCell>
                        <TableCell>{p.forma_envio || '—'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.envio || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={ROTULO_ETAPA[e]}
                            sx={{ bgcolor: COR_ETAPA[e], color: '#fff', fontWeight: 600 }} />
                          {tag && <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.2, mt: 0.25 }}>{tag}</Typography>}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: p.status_mk === 'cancelado' ? 'error.main' : undefined, fontWeight: p.status_mk === 'cancelado' ? 700 : undefined }}>
                          {rotuloStatusMk(p)}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', color: atrasado ? 'error.main' : undefined, fontWeight: atrasado ? 700 : undefined }}>
                          {p.prazo ? dataHoraSP(p.prazo) : 'sem prazo'}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.embalador_id ? p.embalador_nome : ''}</TableCell>
                        <TableCell sx={{ color: 'error.main', fontWeight: 600, fontSize: 12 }}>
                          {p.alertas.map((a) => ROTULO_ALERTA[a]).join(', ')}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
        </Stack>
      )}
    </Drawer>
  )
}
